import { useAuth0 } from '@auth0/auth0-vue';
import { computed, getCurrentInstance } from 'vue';
import { getConfiguredAuth0Client } from './auth0-client';

/**
 * Auth service for handling Auth0 authentication operations
 * This service provides methods for login, logout, and user profile management
 */
export const useAuthService = () => {
  const auth0 = (getCurrentInstance() ? useAuth0() : null) || getConfiguredAuth0Client();
  if (!auth0) {
    throw new Error('Auth0 is not configured. Install the Auth0 plugin before using the auth service.');
  }
  const {
    loginWithRedirect,
    logout,
    user,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently
  } = auth0;

  // Get user profile with formatted metadata
  const userProfile = computed(() => {
    if (!user.value) return null;
    
    return {
      name: user.value.name,
      email: user.value.email,
      picture: user.value.picture,
      sub: user.value.sub,
      // Add other user metadata you need
      ...user.value
    };
  });

  /**
   * Login with Auth0
   * @param {Object} options - Optional login parameters
   */
  const login = (options = {}) => {
    return loginWithRedirect(options);
  };

  /**
   * Sign up with Auth0
   */
  const signup = (options = {}) => {
    return loginWithRedirect({
      ...options,
      authorizationParams: {
        ...options.authorizationParams,
        screen_hint: 'signup',
      }
    });
  };

  /**
   * Login with a specific social provider
   * @param {string} provider - Provider ID (e.g., 'google-oauth2', 'facebook')
   */
  const socialLogin = (provider) => {
    return loginWithRedirect({
      authorizationParams: {
        connection: provider
      }
    });
  };

  /**
   * Logout from Auth0
   * Stores the current URL and redirects back to it after logout
   */
  const logoutUser = () => {
    // Store the current full URL (including path and query parameters) before logout
    const currentUrl = window.location.href;
    localStorage.setItem('logoutReturnUrl', currentUrl);
    
    // Use environment-aware base URL for logout redirect
    const returnTo = process.env.VUE_APP_BASE_URL || window.location.origin;
    return logout({ logoutParams: { returnTo } });
  };

  /**
   * Get the stored logout return URL and clear it from storage
   * @returns {string|null} The stored return URL or null if none exists
   */
  const getAndClearLogoutReturnUrl = () => {
    const returnUrl = localStorage.getItem('logoutReturnUrl');
    if (returnUrl) {
      localStorage.removeItem('logoutReturnUrl');
      return returnUrl;
    }
    return null;
  };

  /**
   * Get an access token for API calls
   * @returns {Promise<string>} Access token
   */
  const getToken = async () => {
      return await getAccessTokenSilently();
  };

  return {
    login,
    signup,
    socialLogin,
    logoutUser,
    getToken,
    getAndClearLogoutReturnUrl,
    userProfile,
    isAuthenticated,
    isLoading
  };
};
